import { ArrowLeft, ChevronDown, Check, Building2, FolderOpen, RefreshCw, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../ui/common-ui/dropdown-menu";
import type { CloudType, Tenant, CloudResource } from "../types/cloud";

// Import cloud logos
import awsLogo from "../assets/aws-logo.svg";
import gcpLogo from "../assets/gcp-logo.svg";
import azureLogo from "../assets/azure-logo.svg";

export interface CloudConfig {
  type: CloudType;
  name: string;
  resourceLabel: string;
  logo: string;
}

export const cloudConfigs: CloudConfig[] = [
  { type: "AWS", name: "AWS", resourceLabel: "Account", logo: awsLogo },
  { type: "GCP", name: "GCP", resourceLabel: "Project", logo: gcpLogo },
  { type: "AZURE", name: "Azure", resourceLabel: "Subscription", logo: azureLogo },
];

interface CloudSelectorProps {
  tenantList: Tenant[];
  selectedTenant: Tenant | null;
  selectedCloud: CloudType;
  selectedResource: CloudResource | null;
  cloudResources: Record<CloudType, CloudResource[]>;
  onTenantChange: (tenant: Tenant) => void;
  onCloudChange: (cloud: CloudType) => void;
  onResourceChange: (resource: CloudResource) => void;
  onRefreshSnapshots?: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  actionButton?: React.ReactNode;
  hideCloudSwitcher?: boolean;
  onBack?: () => void;
}

export function CloudSelector({
  tenantList,
  selectedTenant,
  selectedCloud,
  selectedResource,
  cloudResources,
  onTenantChange,
  onCloudChange,
  onResourceChange,
  onRefreshSnapshots,
  isRefreshing = false,
  isLoading = false,
  actionButton,
  hideCloudSwitcher = false,
  onBack,
}: CloudSelectorProps) {
  const currentCloudConfig = cloudConfigs.find((c) => c.type === selectedCloud)!;
  const currentResources = cloudResources[selectedCloud] || [];

  return (
    <div className="w-full relative flex items-center">
      <div className="relative z-50 w-full flex items-center p-3 rounded-2xl bg-card backdrop-blur-xl border border-border/50 shadow-lg shadow-sky-500/5 gap-4 flex-wrap lg:gap-6">

        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            title="Go back"
            className="flex-none flex items-center justify-center w-10 h-10 mt-5 rounded-xl bg-card border border-border/60 text-muted-foreground shadow-sm transition-all duration-300 hover:text-foreground hover:border-sky-500/30 hover:bg-primary/5 hover:shadow-md active:scale-[0.98]"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}

        {/* Tenant */}
        <div className="flex flex-col gap-1.5 min-w-[180px] w-[240px]">
          <div className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1.5 px-1">
            <Building2 className="w-3.5 h-3.5" />
            Tenant
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full group flex items-center justify-between px-4 py-2.5 rounded-xl bg-card border border-border/50 hover:border-sky-500/30 transition-all duration-300 shadow-sm cursor-pointer">
                <span className="text-[14px] font-medium text-foreground group-hover:text-primary transition-colors truncate">
                  {selectedTenant?.tenantName || "Select tenant"}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-y-0.5 flex-shrink-0 ml-2" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="min-w-[280px] p-2 bg-popover/95 backdrop-blur-xl border-border/50 shadow-xl shadow-black/10 rounded-xl"
            >
              <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-gradient-to-r from-primary/10 to-transparent">
                <Sparkles className="h-4 w-4 text-sky-500" />
                <span className="text-[13px] font-semibold text-sky-500">Select Tenant</span>
              </div>
              {tenantList.length === 0 ? (
                <div className="px-3 py-4 text-[14px] text-muted-foreground text-center rounded-lg bg-muted/30">
                  No tenants available
                </div>
              ) : (
                <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                  {tenantList.map((tenant) => {
                    const isSelected = selectedTenant?.tenantName === tenant.tenantName;
                    return (
                      <DropdownMenuItem
                        key={tenant.tenantName}
                        onClick={() => onTenantChange(tenant)}
                        className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${isSelected
                          ? "bg-gradient-to-r from-primary/20 to-primary/10 text-sky-500"
                          : "hover:bg-accent/50"
                          }`}
                      >
                        <span className={`truncate ${isSelected ? "font-semibold" : "font-medium"}`}>
                          {tenant.tenantName}
                        </span>
                        {isSelected && (
                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 flex-shrink-0">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="hidden sm:block w-px h-12 bg-gradient-to-b from-transparent via-border to-transparent" />

        {!hideCloudSwitcher && (
          <>
            <div className="flex-none flex items-center justify-center gap-1 p-1.5 rounded-xl bg-muted/30 mt-5">
              {cloudConfigs.map((cloud) => {
                const isSelected = selectedCloud === cloud.type;
                const resources = cloudResources[cloud.type] || [];
                const isDisabled = resources.length === 0;

                return (
                  <button
                    key={cloud.type}
                    onClick={() => !isDisabled && onCloudChange(cloud.type)}
                    disabled={isDisabled}
                    className={`
                      relative flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-300 font-medium
                      ${isDisabled
                        ? "opacity-30 cursor-not-allowed"
                        : isSelected
                          ? "bg-card text-foreground shadow-sm ring-1 ring-sky-500/30"
                          : "text-muted-foreground hover:text-foreground hover:bg-card/50"
                      }
                    `}
                    title={isDisabled ? `No ${cloud.resourceLabel}s available` : undefined}
                  >
                    <img
                      src={cloud.logo}
                      alt={cloud.name}
                      className={`h-5 w-auto transition-all duration-300 ${isDisabled
                        ? "opacity-30 grayscale"
                        : isSelected
                          ? "opacity-100 drop-shadow-sm"
                          : "opacity-50 grayscale hover:opacity-70"
                        }`}
                    />
                    <span className="text-[13px] hidden md:inline">{cloud.name}</span>
                  </button>
                );
              })}
            </div>

            <div className="hidden sm:block w-px h-12 bg-gradient-to-b from-transparent via-border to-transparent" />
          </>
        )}

        {/* Cloud Resource */}
        <div className="flex flex-col gap-1.5 min-w-[180px] w-[240px]">
          <div className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1.5 px-1">
            <FolderOpen className="w-3.5 h-3.5" />
            {currentCloudConfig.resourceLabel}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full group flex items-center justify-between px-4 py-2.5 rounded-xl bg-card border border-border/50 hover:border-sky-500/30 transition-all duration-300 shadow-sm cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={currentResources.length === 0}
              >
                <span className="text-[14px] font-medium text-foreground group-hover:text-primary transition-colors truncate">
                  {selectedResource?.name || `Select ${currentCloudConfig.resourceLabel.toLowerCase()}`}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-y-0.5 flex-shrink-0 ml-2" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="min-w-[280px] p-2 bg-popover/95 backdrop-blur-xl border-border/50 shadow-xl shadow-black/10 rounded-xl"
            >
              <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-gradient-to-r from-primary/10 to-transparent">
                <img src={currentCloudConfig.logo} alt={currentCloudConfig.name} className="h-4 w-auto" />
                <span className="text-[13px] font-semibold text-sky-500">
                  Select {currentCloudConfig.resourceLabel}
                </span>
              </div>
              {currentResources.length === 0 ? (
                <div className="px-3 py-4 text-[14px] text-muted-foreground text-center rounded-lg bg-muted/30">
                  No {currentCloudConfig.resourceLabel.toLowerCase()}s available
                </div>
              ) : (
                <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                  {currentResources.map((resource) => {
                    const isResourceSelected = selectedResource?.id === resource.id;
                    return (
                      <DropdownMenuItem
                        key={resource.id}
                        onClick={() => onResourceChange(resource)}
                        className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${isResourceSelected
                          ? "bg-gradient-to-r from-primary/20 to-primary/10 text-sky-500"
                          : "hover:bg-muted/50"
                          }`}
                      >
                        <span className={`truncate ${isResourceSelected ? "font-semibold text-foreground" : "font-medium text-foreground"}`}>
                          {resource.name}
                        </span>
                        {isResourceSelected && (
                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 flex-shrink-0">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {(onRefreshSnapshots || actionButton) && (
          <>
            <div className="hidden lg:block w-px h-12 bg-gradient-to-b from-transparent via-border to-transparent lg:ml-auto" />
            <div className="flex items-center gap-3 ml-auto lg:ml-0">
              {onRefreshSnapshots && (
                <button
                  onClick={onRefreshSnapshots}
                  disabled={isRefreshing || !selectedTenant || !selectedResource}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-[13px] tracking-wide uppercase border transition-all duration-300 ${isRefreshing ? "bg-muted/30 text-muted-foreground border-border/30 cursor-not-allowed" : "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/30 active:scale-[0.98]"}`}
                  title="Update Dashboard Snapshot"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
                  <span className="hidden md:inline">{isRefreshing ? "Updating..." : "Update Snapshot"}</span>
                </button>
              )}
              {actionButton}
            </div>
          </>
        )}
      </div>

      {isLoading && (
        <div className="absolute top-1/2 -right-4 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-sky-500/10 animate-pulse">
          <RefreshCw className="h-4 w-4 animate-spin text-sky-500" />
        </div>
      )}
    </div>
  );
}
